import type { RegisteredRouter } from '@tanstack/react-router'
import { getDeliveryRouteDateSummary } from '@/components/orders/delivery-share.utils'
import { formatCalendarDate, isRecord } from '@/lib/utils'
import { getAdminProductName } from '@/lib/brand'
import { getLocationName } from '@/lib/services/locations/locations.utils'

// A value `cleanTitlePart` turns into title text; empty values are dropped.
export type AdminTitlePart = string | number | null | undefined

// The leaf (page) label comes first, followed by its ancestors.
export type AdminRouteTitleParts = readonly [string, ...string[]]

// The fields of a TanStack route match this module reads. Loader data and
// params arrive untyped from the matched route, so they are narrowed on read.
export type AdminRouteTitleMatch = {
  routeId: string
  loaderData?: unknown
  params?: unknown
}

// TanStack's `head` context, or the breadcrumb context built from the same matches.
export type AdminRouteTitleContext = {
  matches?: readonly AdminRouteTitleMatch[]
  match?: AdminRouteTitleMatch
  loaderData?: unknown
  params?: unknown
}

export type AdminTitleHead = {
  meta: Array<{ title: string }>
}

type AdminRouteId = keyof RegisteredRouter['routesById']

type AdminRouteTitleResolverContext = {
  matches?: readonly AdminRouteTitleMatch[]
  match: AdminRouteTitleMatch
}

type AdminRouteTitleEntry =
  | AdminRouteTitleParts
  | ((context: AdminRouteTitleResolverContext) => AdminRouteTitleParts)

const appTitle = getAdminProductName()
const productsTitle = 'Products'
const availabilityTitle = 'Closures & Availability'

/** A field of untyped route data, or undefined when the value isn't an object. */
export function readField(value: unknown, key: string): unknown {
  return isRecord(value) ? value[key] : undefined
}

function readLoaderField(match: AdminRouteTitleMatch, key: string): unknown {
  return readField(match.loaderData, key)
}

// Untyped loader data and params stringify exactly as template literals did in
// the JavaScript version, whatever their runtime type.
function toTitleText(value: unknown): string {
  return String(value)
}

// Fallback labels such as `Order #37` show the raw route param.
function readParamText(match: AdminRouteTitleMatch, key: string): string {
  return toTitleText(readField(match.params, key))
}

export function cleanTitlePart(part: unknown): string | null {
  if (part === null || part === undefined) {
    return null
  }

  const text = toTitleText(part).trim()

  return text || null
}

export function formatAdminRouteTitle(
  ...parts: ReadonlyArray<AdminTitlePart | readonly AdminTitlePart[]>
): string {
  const titleParts = parts.flat().map(cleanTitlePart).filter(Boolean)

  if (titleParts.at(-1) !== appTitle) {
    titleParts.push(appTitle)
  }

  return titleParts.join(' | ')
}

export function adminTitleHead(
  ...parts: ReadonlyArray<AdminTitlePart | readonly AdminTitlePart[]>
): AdminTitleHead {
  return {
    meta: [{ title: formatAdminRouteTitle(...parts) }],
  }
}

function getMatchByRouteId(
  matches: readonly AdminRouteTitleMatch[] | undefined,
  routeId: AdminRouteId
): AdminRouteTitleMatch | null {
  return matches?.find((match) => match.routeId === routeId) ?? null
}

function getRouteLoaderData(
  matches: readonly AdminRouteTitleMatch[] | undefined,
  routeId: AdminRouteId
): unknown {
  return getMatchByRouteId(matches, routeId)?.loaderData ?? null
}

function getRouteEntityName(value: unknown, fallback: string): string {
  return (
    cleanTitlePart(readField(value, 'name')) ??
    cleanTitlePart(readField(value, 'title')) ??
    cleanTitlePart(readField(value, 'label')) ??
    fallback
  )
}

/** A location for page titles and breadcrumbs, named the way the rest of admin names it. */
export function getLocationTitleLabel(location: unknown, locationId: string): string {
  const address = readField(location, 'address')

  return getLocationName({
    id: Number(locationId),
    address: {
      name: cleanTitlePart(readField(address, 'name')),
      line1: cleanTitlePart(readField(address, 'line1')),
    },
  })
}

export function getDeliveryRouteTitleLabel(deliveryShare: unknown, fallback: string): string {
  if (!deliveryShare) {
    return fallback
  }

  return (
    cleanTitlePart(readField(deliveryShare, 'name')) ??
    cleanTitlePart(getDeliveryRouteDateSummary(deliveryShare)) ??
    fallback
  )
}

function getCustomerTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const customer = readLoaderField(match, 'customer')

  return [
    getRouteEntityName(customer, `Customer #${readParamText(match, 'customerId')}`),
    'Customers',
  ]
}

function getDepositTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  return [`Transfer #${readParamText(match, 'depositId')}`, 'Credit Card Deposit Report', 'Reports']
}

function getCustomerHistoryTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [customer] = getCustomerTitle({ match })

  return ['History', customer, 'Customers']
}

function getDesignTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const design = readLoaderField(match, 'design')

  return [
    getRouteEntityName(design, `Design #${readParamText(match, 'designId')}`),
    'Website Designs',
  ]
}

function getDesignHistoryTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [design] = getDesignTitle({ match })

  return ['History', design, 'Website Designs', 'Website']
}

function getMessageTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const messageName = readField(readLoaderField(match, 'message'), 'name')

  return [
    messageName
      ? `Message from ${toTitleText(messageName)}`
      : `Message #${readParamText(match, 'messageId')}`,
    'Messages',
  ]
}

function getCustomerReviewTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const review = readLoaderField(match, 'review')
  const reviewerName = cleanTitlePart(readField(review, 'reviewer_name'))

  return [
    reviewerName
      ? `Review from ${reviewerName}`
      : `Review #${readParamText(match, 'customerReviewId')}`,
    'Reviews',
  ]
}

function getOrganizationTitle({
  matches,
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const organization =
    readLoaderField(match, 'organization') ??
    readField(
      getRouteLoaderData(matches, '/_authenticated/$orgId/organizations/$organizationId'),
      'organization'
    )

  return [
    getRouteEntityName(organization, `Organization #${readParamText(match, 'organizationId')}`),
    'Organizations',
  ]
}

function getProductTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const product = readLoaderField(match, 'product')

  return [
    getRouteEntityName(product, `Product #${readParamText(match, 'productId')}`),
    productsTitle,
  ]
}

function getProductHistoryTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [product] = getProductTitle({ match })

  return ['History', product, productsTitle]
}

function getAddOnTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const addOn = readLoaderField(match, 'addOn')

  return [getRouteEntityName(addOn, `Add-on #${readParamText(match, 'addOnId')}`), productsTitle]
}

function getAddOnSetTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const addOnSet = readLoaderField(match, 'addOnSet')

  return [
    getRouteEntityName(addOnSet, `Add-on Collection #${readParamText(match, 'addOnSetId')}`),
    'Add-on Collections',
    productsTitle,
  ]
}

function getCollectionTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const collection = readLoaderField(match, 'collection')

  return [
    getRouteEntityName(collection, `Collection #${readParamText(match, 'collectionId')}`),
    'Collections',
    productsTitle,
  ]
}

function getPriceChangeTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const priceAdjustment = readLoaderField(match, 'priceAdjustment')

  return [
    getRouteEntityName(
      priceAdjustment,
      `Price Change #${readParamText(match, 'priceAdjustmentId')}`
    ),
    'Price Changes',
    productsTitle,
  ]
}

function getSalesOrderTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  return [`Order #${readParamText(match, 'salesOrderId')}`, 'Orders']
}

function getFlowerSubscriptionTitle({
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const subscription = readLoaderField(match, 'subscription')
  const packageName = cleanTitlePart(readField(readField(subscription, 'product'), 'name'))

  return [
    packageName ?? `Subscription #${readParamText(match, 'flowerSubscriptionId')}`,
    'Subscriptions',
  ]
}

function getSalesOrderHistoryTitle({
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [salesOrder] = getSalesOrderTitle({ match })

  return ['History', salesOrder, 'Orders']
}

function getSalesOrderEditTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [salesOrder] = getSalesOrderTitle({ match })

  return ['Edit', salesOrder, 'Orders']
}

function getSalesOrderJournalTitle({
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [salesOrder] = getSalesOrderTitle({ match })

  return ['Journal entries', salesOrder, 'Orders']
}

function getSalesSummaryReportTitle({
  matches,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const organization = readField(
    getRouteLoaderData(matches, '/_authenticated/$orgId'),
    'organization'
  )

  return [
    readField(organization, 'type') === 'global' ? 'Global Payments Collected' : 'Sales Statistics',
    'Reports',
  ]
}

function getDeliveryRouteTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const deliveryShare = readLoaderField(match, 'deliveryShare')

  return [
    getDeliveryRouteTitleLabel(
      deliveryShare,
      `Delivery Route #${readParamText(match, 'deliveryShareId')}`
    ),
    'Deliveries',
    'Orders',
  ]
}

function getDeliveryRouteEditTitle({
  matches,
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const deliveryShare =
    readLoaderField(match, 'deliveryShare') ??
    readField(
      getRouteLoaderData(
        matches,
        '/_authenticated/$orgId/sales-orders/delivery-routes/$deliveryShareId'
      ),
      'deliveryShare'
    )

  return [
    'Edit',
    getDeliveryRouteTitleLabel(
      deliveryShare,
      `Delivery Route #${readParamText(match, 'deliveryShareId')}`
    ),
    'Deliveries',
    'Orders',
  ]
}

function getDomainTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const domain = readLoaderField(match, 'domain')

  return [
    cleanTitlePart(readField(domain, 'domain')) ??
      cleanTitlePart(readField(domain, 'url')) ??
      getRouteEntityName(domain, `Domain #${readParamText(match, 'domainId')}`),
    'Domains',
    'Website',
  ]
}

function getCustomPageTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  return [
    getRouteEntityName(
      readLoaderField(match, 'customPage'),
      `Custom Page #${readParamText(match, 'customPageId')}`
    ),
    'Custom Pages',
    'Website',
  ]
}

function getLocationTitle({
  matches,
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const location = readField(
    getRouteLoaderData(matches, '/_authenticated/$orgId/settings/locations/$locationId'),
    'location'
  )

  return [
    getLocationTitleLabel(location, readParamText(match, 'locationId')),
    'Locations',
    'Settings',
  ]
}

function getLocationDeliveryTitle({
  matches,
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [location] = getLocationTitle({ matches, match })

  return ['Delivery Settings', location, 'Locations', 'Settings']
}

function getLocationHoursTitle({
  matches,
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [location] = getLocationTitle({ matches, match })

  return ['Store Hours', location, 'Locations', 'Settings']
}

function getLocationHistoryTitle({
  matches,
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [location] = getLocationTitle({ matches, match })

  return ['History', location, 'Locations', 'Settings']
}

function getNotificationRecipientTitle({
  match,
}: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const recipient = readLoaderField(match, 'recipient')

  return [
    cleanTitlePart(readField(recipient, 'label')) ??
      cleanTitlePart(readField(recipient, 'value')) ??
      `Recipient #${readParamText(match, 'recipientId')}`,
    'Notifications',
    'Settings',
  ]
}

function getTerminalTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const terminal = readLoaderField(match, 'terminal')

  return [
    getRouteEntityName(terminal, `Terminal #${readParamText(match, 'terminalId')}`),
    'Card Readers',
    'Settings',
  ]
}

function getUserTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const user = readLoaderField(match, 'user')

  return [getRouteEntityName(user, `User #${readParamText(match, 'userId')}`), 'Users', 'Settings']
}

function getUserHistoryTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const [user] = getUserTitle({ match })

  return ['History', user, 'Users', 'Settings']
}

function getRolePermissionsTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const role = readLoaderField(match, 'role')
  const user = readLoaderField(match, 'user')

  return [
    'Permissions',
    cleanTitlePart(readField(role, 'display_name')) ??
      getRouteEntityName(role, `Role #${readParamText(match, 'roleId')}`),
    getRouteEntityName(user, `User #${readParamText(match, 'userId')}`),
    'Users',
    'Settings',
  ]
}

function getAlbumTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const album = readLoaderField(match, 'album')

  return [
    getRouteEntityName(album, `Album #${readParamText(match, 'albumId')}`),
    'Gallery',
    'Website',
  ]
}

function getStockImageTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  const stockImage = readLoaderField(match, 'stockImage')
  const mediaName = cleanTitlePart(readField(readField(stockImage, 'latest_media'), 'name'))

  return [
    getRouteEntityName(
      stockImage,
      mediaName ?? `Stock Image #${readParamText(match, 'stockImageId')}`
    ),
    'Stock Images',
  ]
}

function getAppUpdateTitle({ match }: AdminRouteTitleResolverContext): AdminRouteTitleParts {
  return [getRouteEntityName(readLoaderField(match, 'appUpdate'), 'App Update'), "What's New"]
}

// Breadcrumb leaf labels also come from this registry; a route-local head()
// declaration alone only updates the browser tab title. Keys are checked
// against the generated route tree, so a renamed or removed route fails tsc.
const adminRouteTitleByRouteId: Readonly<Partial<Record<AdminRouteId, AdminRouteTitleEntry>>> = {
  '/_authenticated/$orgId/reports/phone-clicks': ['Mobile Call Report', 'Reports'],
  '/_authenticated/$orgId/reports/phone-clicks/': ['Mobile Call Report', 'Reports'],
  '/_authenticated/$orgId': ['Dashboard'],
  '/_authenticated/$orgId/admin': ['Dashboard'],
  '/_authenticated/$orgId/api-tokens/': ['API Tokens'],
  '/_authenticated/$orgId/contracts/': ['Pricing'],
  '/_authenticated/$orgId/contracts/create': ['Create Pricing', 'Pricing'],
  '/_authenticated/$orgId/pricing/': ['Pricing'],
  '/_authenticated/$orgId/pricing/create': ['Create Pricing', 'Pricing'],
  '/_authenticated/$orgId/customers/': ['Customers'],
  '/_authenticated/$orgId/customers/$customerId': getCustomerTitle,
  '/_authenticated/$orgId/customers/$customerId_/history': getCustomerHistoryTitle,
  '/_authenticated/$orgId/customers/create': ['Create Customer', 'Customers'],
  '/_authenticated/$orgId/customer-reviews/': ['Reviews'],
  '/_authenticated/$orgId/customer-reviews/$customerReviewId': getCustomerReviewTitle,
  '/_authenticated/$orgId/designs/': ['Website Designs'],
  '/_authenticated/$orgId/designs/$designId': getDesignTitle,
  '/_authenticated/$orgId/designs/$designId_/history': getDesignHistoryTitle,
  '/_authenticated/$orgId/designs/create': ['Create Design', 'Website Designs'],
  '/_authenticated/$orgId/activity': ['Messages'],
  '/_authenticated/$orgId/gallery/album/$albumId': getAlbumTitle,
  '/_authenticated/$orgId/gallery/album/create': ['Create Album', 'Gallery', 'Website'],
  '/_authenticated/$orgId/imports': ['Imports'],
  '/_authenticated/$orgId/imports/': ['Imports'],
  '/_authenticated/$orgId/imports/customer-import': ['Customer Import', 'Imports'],
  '/_authenticated/$orgId/imports/fsn-import': ['Flower Shop Network Import', 'Imports'],
  '/_authenticated/$orgId/imports/product-import': ['Product Import', 'Imports'],
  '/_authenticated/$orgId/messages/': ['Messages'],
  '/_authenticated/$orgId/messages/$messageId': getMessageTitle,
  '/_authenticated/$orgId/messages/create': ['Create Inquiry', 'Messages'],
  '/_authenticated/$orgId/marketing/': ['Marketing'],
  '/_authenticated/$orgId/marketing/discounts/': ['Discount Codes', 'Marketing'],
  '/_authenticated/$orgId/notications-center': ['Messages'],
  '/_authenticated/$orgId/organizations/': ['Organizations'],
  '/_authenticated/$orgId/organizations/$organizationId': getOrganizationTitle,
  '/_authenticated/$orgId/organizations/$organizationId/': getOrganizationTitle,
  '/_authenticated/$orgId/organizations/create': ['Create Organization', 'Organizations'],
  '/_authenticated/$orgId/products/': [productsTitle],
  '/_authenticated/$orgId/products/$productId': getProductTitle,
  '/_authenticated/$orgId/products/$productId_/history': getProductHistoryTitle,
  '/_authenticated/$orgId/products/add-ons/$addOnId': getAddOnTitle,
  '/_authenticated/$orgId/products/add-ons/create': ['Create Product', productsTitle],
  '/_authenticated/$orgId/products/add-on-sets/': ['Add-on Collections', productsTitle],
  '/_authenticated/$orgId/products/add-on-sets/$addOnSetId': getAddOnSetTitle,
  '/_authenticated/$orgId/products/add-on-sets/create': [
    'Create Add-on Collection',
    'Add-on Collections',
    productsTitle,
  ],
  '/_authenticated/$orgId/products/all-products': ['All Products', productsTitle],
  '/_authenticated/$orgId/products/catalog-builder': ['Catalog Builder', productsTitle],
  '/_authenticated/$orgId/products/collections/': ['Collections', productsTitle],
  '/_authenticated/$orgId/products/collections/$collectionId': getCollectionTitle,
  '/_authenticated/$orgId/products/collections/create': ['Create Collection', productsTitle],
  '/_authenticated/$orgId/products/collections/import': ['Import Collections', productsTitle],
  '/_authenticated/$orgId/products/create': ['Create Product', productsTitle],
  '/_authenticated/$orgId/products/inventory': ['Inventory', productsTitle],
  '/_authenticated/$orgId/products/price-changes/': ['Price Changes', productsTitle],
  '/_authenticated/$orgId/products/price-changes/$priceAdjustmentId': getPriceChangeTitle,
  '/_authenticated/$orgId/products/price-changes/create': ['Create Price Change', productsTitle],
  '/_authenticated/$orgId/reports/': ['Reports'],
  '/_authenticated/$orgId/reports/cash-drawer/': ['Cash Drawer Report', 'Reports'],
  '/_authenticated/$orgId/reports/external-orders': ['Incoming Wire Orders', 'Reports'],
  '/_authenticated/$orgId/reports/florist-map/': ['Florist Map', 'Reports'],
  '/_authenticated/$orgId/reports/florist-performance/': ['Florist Performance', 'Reports'],
  '/_authenticated/$orgId/reports/sales-statistics': getSalesSummaryReportTitle,
  '/_authenticated/$orgId/reports/sales-statistics/': getSalesSummaryReportTitle,
  '/_authenticated/$orgId/money': ['Money'],
  '/_authenticated/$orgId/money/': ['Money'],
  '/_authenticated/$orgId/money/cash-drawer-balancing': ['Cash Drawer Balancing', 'Money'],
  '/_authenticated/$orgId/reports/sales-summary/': ['Sales Summary', 'Reports'],
  '/_authenticated/$orgId/reports/sales-summary/$date': ({ match }) => {
    // The `$date` segment has no params parser, so the router hands over the URL string.
    const date = readField(match.params, 'date')

    return [
      formatCalendarDate(typeof date === 'string' ? date : undefined),
      'Sales Summary',
      'Reports',
    ]
  },
  '/_authenticated/$orgId/money/statements/': ['Customer Statements', 'Money'],
  '/_authenticated/$orgId/money/customer-balances': ['Customer Balances', 'Money'],
  '/_authenticated/$orgId/money/customer-balances/': ['Customer Balances', 'Money'],
  '/_authenticated/$orgId/reports/accounting-journal': ['Journal entries', 'Reports'],
  '/_authenticated/$orgId/reports/accounting-journal/': ['Journal entries', 'Reports'],
  '/_authenticated/$orgId/reports/closed-days': ['Closed Days Report', 'Reports'],
  '/_authenticated/$orgId/reports/closed-days/': ['Closed Days Report', 'Reports'],
  '/_authenticated/$orgId/reports/deposits': ['Credit Card Deposit Report', 'Reports'],
  '/_authenticated/$orgId/reports/deposits/': ['Credit Card Deposit Report', 'Reports'],
  '/_authenticated/$orgId/reports/deposits/$depositId': getDepositTitle,
  '/_authenticated/$orgId/reports/platform-ledger': ['Platform Ledger', 'Reports'],
  '/_authenticated/$orgId/reports/platform-ledger/': ['Platform Ledger', 'Reports'],
  '/_authenticated/$orgId/reports/sales-tax': ['Sales Tax Report', 'Reports'],
  '/_authenticated/$orgId/reports/sales-tax/': ['Sales Tax Report', 'Reports'],
  '/_authenticated/$orgId/reports/tips/': ['Tips Report', 'Reports'],
  '/_authenticated/$orgId/sales-orders/': ['Orders'],
  '/_authenticated/$orgId/sales-orders/wire-in': ['Orders Waiting', 'Orders'],
  '/_authenticated/$orgId/sales-orders/$salesOrderId': getSalesOrderTitle,
  '/_authenticated/$orgId/sales-orders/$salesOrderId_/edit': getSalesOrderEditTitle,
  '/_authenticated/$orgId/sales-orders/$salesOrderId_/history': getSalesOrderHistoryTitle,
  '/_authenticated/$orgId/sales-orders/$salesOrderId_/journal': getSalesOrderJournalTitle,
  '/_authenticated/$orgId/sales-orders/create': ['Create Order', 'Orders'],
  '/_authenticated/$orgId/sales-orders/drafts': ['Draft Orders', 'Orders'],
  '/_authenticated/$orgId/sales-orders/delivery-routes/': ['Deliveries', 'Orders'],
  '/_authenticated/$orgId/sales-orders/delivery-routes/$deliveryShareId': getDeliveryRouteTitle,
  '/_authenticated/$orgId/sales-orders/delivery-routes/$deliveryShareId/edit':
    getDeliveryRouteEditTitle,
  '/_authenticated/$orgId/sales-orders/delivery-routes/create': ['Create Delivery Route', 'Orders'],
  '/_authenticated/$orgId/subscriptions/': ['Subscriptions'],
  '/_authenticated/$orgId/subscriptions/$flowerSubscriptionId': getFlowerSubscriptionTitle,
  '/_authenticated/$orgId/settings/': ['Settings'],
  '/_authenticated/$orgId/settings/cash-drawer/': ['Cash Drawer', 'Settings'],
  '/_authenticated/$orgId/settings/cash-drawer/history': ['History', 'Cash Drawer', 'Settings'],
  '/_authenticated/$orgId/settings/billing/': ['Billing Settings', 'Settings'],
  '/_authenticated/$orgId/settings/billing/history': ['History', 'Billing Settings', 'Settings'],
  '/_authenticated/$orgId/settings/checkout/': ['Online Checkout', 'Settings'],
  '/_authenticated/$orgId/settings/fees/': ['Shop fees', 'Settings'],
  '/_authenticated/$orgId/settings/fees/history': ['History', 'Shop fees', 'Settings'],
  '/_authenticated/$orgId/settings/taxes/': ['Taxes', 'Settings'],
  '/_authenticated/$orgId/settings/taxes/history': ['History', 'Taxes', 'Settings'],
  '/_authenticated/$orgId/settings/subscriptions/': ['Subscription Settings', 'Settings'],
  '/_authenticated/$orgId/settings/email-marketing/': ['Email Marketing', 'Marketing'],
  '/_authenticated/$orgId/settings/locations/': ['Locations', 'Settings'],
  '/_authenticated/$orgId/settings/locations/$locationId': getLocationTitle,
  '/_authenticated/$orgId/settings/locations/$locationId/': getLocationTitle,
  '/_authenticated/$orgId/settings/locations/$locationId/deliveries/': getLocationDeliveryTitle,
  '/_authenticated/$orgId/settings/locations/$locationId/history': getLocationHistoryTitle,
  '/_authenticated/$orgId/settings/locations/$locationId/hours/': getLocationHoursTitle,
  '/_authenticated/$orgId/availability/': [availabilityTitle],
  '/_authenticated/$orgId/settings/locations/create': ['Create Location', 'Locations', 'Settings'],
  '/_authenticated/$orgId/settings/notifications/': ['Notifications', 'Settings'],
  '/_authenticated/$orgId/settings/notifications/$recipientId': getNotificationRecipientTitle,
  '/_authenticated/$orgId/settings/notifications/create': [
    'Create Recipient',
    'Notifications',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/stripe/': ['Stripe', 'Settings'],
  '/_authenticated/$orgId/settings/stripe/account-management': [
    'Account Management',
    'Stripe',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/stripe/onboarding': ['Stripe Onboarding', 'Settings'],
  '/_authenticated/$orgId/settings/stripe/upgrade': ['Account Upgrade', 'Stripe', 'Settings'],
  '/_authenticated/$orgId/settings/stripe/payouts': ['Payouts', 'Stripe', 'Settings'],
  '/_authenticated/$orgId/settings/stripe/payments': ['Payments', 'Stripe', 'Settings'],
  '/_authenticated/$orgId/settings/stripe/payment-methods': [
    'Payment methods',
    'Stripe',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/stripe/disputes': ['Disputes', 'Stripe', 'Settings'],
  '/_authenticated/$orgId/settings/stripe/card-readers': ['Buy Card Readers', 'Stripe', 'Settings'],
  '/_authenticated/$orgId/settings/terminals/': ['Card Readers', 'Settings'],
  '/_authenticated/$orgId/settings/terminals/$terminalId': getTerminalTitle,
  '/_authenticated/$orgId/settings/terminals/create': [
    'Register New Terminal',
    'Card Readers',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/integrations/': ['Integrations', 'Settings'],
  '/_authenticated/$orgId/settings/integrations/condolences/': [
    'Condolences',
    'Integrations',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/integrations/fsn-network/': [
    'Flower Shop Network',
    'Integrations',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/integrations/snapdragon/': [
    'Snapdragon',
    'Integrations',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/integrations/snapdragon/history': [
    'History',
    'Snapdragon',
    'Integrations',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/integrations/google-analytics/': [
    'Google Analytics',
    'Integrations',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/integrations/google-tag-manager/': [
    'Google Tag Manager',
    'Integrations',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/integrations/meta-pixel/': [
    'Meta Pixel',
    'Integrations',
    'Settings',
  ],
  '/_authenticated/$orgId/settings/supertickets/': ['Supertickets', 'Settings'],
  '/_authenticated/$orgId/settings/snapdragon/': ['Snapdragon', 'Settings'],
  '/_authenticated/$orgId/stock-images/': ['Stock Images'],
  '/_authenticated/$orgId/stock-images/$stockImageId': getStockImageTitle,
  '/_authenticated/$orgId/stock-images/create': ['Create Stock Image', 'Stock Images'],
  '/_authenticated/$orgId/seasonal-content/': ['Seasonal Content'],
  '/_authenticated/$orgId/seasonal-content/create': ['Create Seasonal Design', 'Seasonal Content'],
  '/_authenticated/$orgId/seasonal-content/$seasonalDesignId': [
    'Edit Seasonal Design',
    'Seasonal Content',
  ],
  '/_authenticated/$orgId/whats-new/': ["What's New"],
  '/_authenticated/$orgId/whats-new/create': ['Create App Update', "What's New"],
  '/_authenticated/$orgId/whats-new/$appUpdateId': getAppUpdateTitle,
  '/_authenticated/$orgId/users/': ['Users', 'Settings'],
  '/_authenticated/$orgId/users/$userId/': getUserTitle,
  '/_authenticated/$orgId/users/$userId/history': getUserHistoryTitle,
  '/_authenticated/$orgId/users/$userId/roles/$roleId': getRolePermissionsTitle,
  '/_authenticated/$orgId/users/create': ['Create User', 'Users', 'Settings'],
  '/_authenticated/$orgId/website': ['Website'],
  '/_authenticated/$orgId/website/': ['Website'],
  '/_authenticated/$orgId/website/builder': ['Website Builder', 'Website'],
  '/_authenticated/$orgId/website/custom-pages/': ['Custom Pages', 'Website'],
  '/_authenticated/$orgId/website/custom-pages/$customPageId': getCustomPageTitle,
  '/_authenticated/$orgId/website/custom-pages/create': ['New Page', 'Custom Pages', 'Website'],
  '/_authenticated/$orgId/website/domains/': ['Domains', 'Website'],
  '/_authenticated/$orgId/website/domains/$domainId': getDomainTitle,
  '/_authenticated/$orgId/website/domains/create': ['Create Domain', 'Domains', 'Website'],
  '/_authenticated/$orgId/website/overview': ['Website'],
  '/_authenticated/$orgId/website/url-redirects/': ['URL Redirects', 'Website'],
  '/_authenticated/$orgId/website/url-redirects/flower-shop-network': [
    'Flower Shop Network',
    'URL Redirects',
    'Website',
  ],
}

function withRouteHeadData(
  titleMatch: AdminRouteTitleMatch | null,
  context: AdminRouteTitleContext
): AdminRouteTitleMatch | null {
  if (!titleMatch || context.match?.routeId !== titleMatch.routeId) {
    return titleMatch
  }

  return {
    ...titleMatch,
    loaderData: {
      ...(isRecord(context.loaderData) ? context.loaderData : {}),
      ...(isRecord(titleMatch.loaderData) ? titleMatch.loaderData : {}),
    },
    params: {
      ...(isRecord(context.params) ? context.params : {}),
      ...(isRecord(titleMatch.params) ? titleMatch.params : {}),
    },
  }
}

// Plain string lookup for runtime route ids; the typed map above stays the source of truth.
const adminRouteTitleEntryByRouteId: Readonly<Partial<Record<string, AdminRouteTitleEntry>>> =
  adminRouteTitleByRouteId

function resolveAdminRouteTitleParts(context: AdminRouteTitleContext = {}): AdminRouteTitleParts {
  const { matches, match } = context
  const titleMatch =
    [...(matches ?? [])]
      .reverse()
      .find((candidate) => adminRouteTitleEntryByRouteId[candidate.routeId]) ??
    (match?.routeId ? match : null)
  const routeHeadMatch = withRouteHeadData(titleMatch, context)

  if (!routeHeadMatch) {
    return ['Dashboard']
  }

  const titleResolver = adminRouteTitleEntryByRouteId[routeHeadMatch.routeId]

  if (typeof titleResolver === 'function') {
    return titleResolver({ matches, match: routeHeadMatch })
  }

  return titleResolver ?? ['Dashboard']
}

export function getAdminRouteTitleParts(
  context: AdminRouteTitleContext = {}
): [string, ...string[]] {
  return [...resolveAdminRouteTitleParts(context)]
}

export function getAdminRouteTitle(context: AdminRouteTitleContext = {}): string {
  return formatAdminRouteTitle(getAdminRouteTitleParts(context))
}

export function adminRouteTitleHead(context: AdminRouteTitleContext = {}): AdminTitleHead {
  return adminTitleHead(getAdminRouteTitleParts(context))
}
